import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  DollarSign, TrendingUp, Award, Plus, Eye, MousePointer, Clock,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const AdCreditsDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [qualification, setQualification] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showCreateAd, setShowCreateAd] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    
    try {
      await Promise.all([
        loadAccount(),
        loadQualification(),
        loadMyAds(),
        loadTransactions()
      ]);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const loadAccount = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/ad-credits/account`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setAccount(data.account);
  };

  const loadQualification = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/ad-credits/qualification`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setQualification(data);
  };

  const loadMyAds = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/ad-credits/my-ads`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setMyAds(data.ads);
  };

  const loadTransactions = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/ad-credits/transactions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setTransactions(data.transactions);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="w-4 h-4" /> },
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
      paused: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <AlertCircle className="w-4 h-4" /> },
      expired: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="w-4 h-4" /> },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="w-4 h-4" /> }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`${badge.bg} ${badge.text} px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1`}>
        {badge.icon}
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold mb-2">💰 Ad Credits Dashboard</h1>
          <p className="text-purple-100">
            Manage your advertising credits and marketplace presence
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-500" />
              <span className="text-3xl font-bold text-gray-900">{account?.available_ad_credits || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Available Credits</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-blue-500" />
              <span className="text-3xl font-bold text-gray-900">{account?.total_earned || 0}</span>
            </div>
            <p className="text-gray-600 text-sm">Total Earned</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Eye className="w-8 h-8 text-purple-500" />
              <span className="text-3xl font-bold text-gray-900">{myAds.filter(ad => ad.status === 'active').length}</span>
            </div>
            <p className="text-gray-600 text-sm">Active Ads</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-8 h-8 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">{account?.ad_matrix_active ? 'Active' : 'Locked'}</span>
            </div>
            <p className="text-gray-600 text-sm">Ad Matrix Status</p>
          </div>
        </div>

        {/* Ad Matrix Qualification */}
        {!account?.ad_matrix_active && qualification && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold">$100 Ad Matrix - Unlock Premium Advertising</h2>
            </div>
            
            {qualification.qualified ? (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-bold text-green-900 text-lg">🎉 You're Qualified!</p>
                    <p className="text-green-700">You've completed {qualification.pif_count} PIFs and can now enter the Ad Matrix</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">What You Get:</h3>
                  <ul className="list-disc ml-6 space-y-1 text-sm">
                    <li><strong>1,000 Ad Credits</strong> on entry</li>
                    <li><strong>500 Credits per referral</strong> in Ad Matrix</li>
                    <li>Place <strong>video ads</strong> on marketplace</li>
                    <li>Access to <strong>primary spotlight</strong> position</li>
                    <li>Send emails to <strong>network safe list</strong></li>
                    <li>Half-page ad placements</li>
                  </ul>
                </div>
                <button className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 w-full">
                  Purchase $100 Ad Matrix Entry
                </button>
              </div>
            ) : (
              <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                  <div>
                    <p className="font-bold text-yellow-900 text-lg">Not Yet Qualified</p>
                    <p className="text-yellow-700">{qualification.reason}</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm mb-2">Progress to Ad Matrix:</p>
                  <div className="bg-gray-200 rounded-full h-4 mb-2">
                    <div 
                      className="bg-purple-600 rounded-full h-4"
                      style={{ width: `${(qualification.pif_count / 2) * 100}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600">{qualification.pif_count} / 2 PIFs Complete</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Advertisements */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">My Advertisements</h2>
            <button
              onClick={() => setShowCreateAd(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Ad
            </button>
          </div>

          {myAds.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg mb-2">No advertisements yet</p>
              <p className="text-sm">Create your first ad to reach thousands of members!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {myAds.map((ad) => (
                <div key={ad.id} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{ad.title || ad.text_line1}</h3>
                      <p className="text-sm text-gray-600">{ad.ad_type.replace('_', ' ')}</p>
                    </div>
                    {getStatusBadge(ad.status)}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    <div>
                      <p className="text-gray-500">Impressions</p>
                      <p className="font-semibold">{ad.impressions || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Clicks</p>
                      <p className="font-semibold">{ad.clicks || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">CTR</p>
                      <p className="font-semibold">{ad.ctr?.toFixed(2) || 0}%</p>
                    </div>
                  </div>

                  {ad.status === 'rejected' && ad.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                      <p className="font-semibold">Rejection Reason:</p>
                      <p>{ad.rejection_reason}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Recent Transactions</h2>
          
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.slice(0, 10).map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-semibold">{tx.description}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold text-lg ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </p>
                    <p className="text-xs text-gray-500">
                      Balance: {tx.balance_after}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdCreditsDashboard;
